import type { z } from 'zod';
import { functions } from '../../../firebase';

export class AlertApiError extends Error {
  constructor(message: string, public code: string = '') {
    super(message);
    this.name = 'AlertApiError';
    Object.setPrototypeOf(this, AlertApiError.prototype);
  }
}

export function isVersionConflict(error: unknown): boolean {
  return error instanceof AlertApiError && ['aborted', 'conflict', '409'].includes(error.code);
}

function readableError(error: unknown): AlertApiError {
  if (error instanceof AlertApiError) return error;
  const details = error as { code?: string; message?: string } | null;
  const code = String(details?.code || '').replace(/^functions\//, '');
  if (['aborted', 'conflict', '409'].includes(code)) {
    return new AlertApiError(
      'En anden har ændret oplysningerne. Hent den seneste version og prøv igen.',
      code
    );
  }
  if (['permission-denied', 'unauthenticated', '401', '403'].includes(code)) {
    return new AlertApiError(
      'Du har ikke adgang til alarmstyringen. Kontrollér din adgang og prøv igen.',
      code
    );
  }
  return new AlertApiError(
    details?.message || 'Alarmstyringen kunne ikke kontaktes. Prøv igen.',
    code
  );
}

export async function callAlerts<T>(
  requestType: string,
  payload: unknown,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>
): Promise<T> {
  try {
    const response = await functions.httpsCallable('settings')({ requestType, payload });
    const data = response.data;
    if (data && typeof data === 'object') {
      const status = data.httpErrorCode?.status;
      if ((status && status !== 200) || data.error || data.success === false) {
        throw readableError({
          code: status ? String(status) : data.code,
          message:
            typeof data.message === 'string'
              ? data.message
              : 'Alarmstyringen returnerede en fejl. Prøv igen.',
        });
      }
    }
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new AlertApiError(
        'Svaret fra alarmstyringen er ufuldstændigt. Opdatér og prøv igen.',
        'invalid-response'
      );
    }
    return parsed.data;
  } catch (error) {
    throw readableError(error);
  }
}
